/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import { styleguideFontFamily as FONT, withOpacity } from '@kaiizen/kai-shiki/core';

import { pxToRem } from '@/Client/Styles/pxToRem';
import type { PhotoManagerSection } from '../photoManagerModel';
import { useGalleryPhotoUpload } from '../useGalleryPhotoUpload';

/**
 * The add affordance is a tile the same size as the thing it's asking for, closing the
 * section's grid — and a vacant section is that tile alone. (Mockup 30: the strip was a
 * different object doing the same job, so a section changed shape the moment its first
 * photo landed.)
 */
const DROP_WRAP_STYLE = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const DROP_TILE_STYLE = css`
  label: PHOTO_MANAGER_DROP_TILE;
  aspect-ratio: 4 / 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  border: 1.5px dashed ${withOpacity(COLORS.white, 0.32)};
  background: ${withOpacity(COLORS.white, 0.04)};
  color: ${withOpacity(COLORS.white, 0.6)};
  font-family: ${FONT.body};
  text-align: center;
  cursor: pointer;

  &:hover {
    border-color: ${withOpacity(COLORS.white, 0.75)};
    background: ${withOpacity(COLORS.white, 0.1)};
    color: ${COLORS.white};
  }
`;

const DROP_TITLE_STYLE = css`
  font-size: ${pxToRem(12.5)};
  font-weight: 600;
  line-height: 1.2;
`;

const DROP_SUB_STYLE = css`
  font-size: ${pxToRem(11.5)};
  opacity: 0.75;

  b {
    color: ${COLORS.white};
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

/** Reserves the line the photos spend on their date, so a row with the drop tile matches a row of photos. */

const DROP_CAP_STYLE = css`
  label: PHOTO_MANAGER_DROP_CAP;
  display: block;
  font-family: ${FONT.body};
  font-size: ${pxToRem(11.5)};
  line-height: 1.35;
  visibility: hidden;
`;

/** A vacant section on a read-only scan: no affordance, just the fact. */

/**
 * One upload input per section, rendered as the drop tile closing its grid. The destination
 * rides in the hook's closure, so the section you add from IS the answer to "which task is
 * this for?" — no picker ever asks.
 */
export const SectionUpload = ({ section }: { section: PhotoManagerSection }) => {
  const { inputRef, openFilePicker, onSelectFiles } = useGalleryPhotoUpload(section);
  return (
    <>
      <input
        accept='image/*'
        data-testid='photo-manager-file-input'
        hidden
        multiple
        onChange={onSelectFiles}
        ref={inputRef}
        type='file'
      />
      <span css={DROP_WRAP_STYLE}>
        <button
          css={DROP_TILE_STYLE}
          data-testid='photo-manager-add-tile'
          onClick={openFilePicker}
          title='Drag photos here, or click to browse'
          type='button'
        >
          <svg
            aria-hidden='true'
            fill='none'
            height='20'
            stroke='currentColor'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='1.5'
            viewBox='0 0 24 24'
            width='20'
          >
            <path d='M12 16V4M12 4l-5 5M12 4l5 5' />
            <path d='M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3' />
          </svg>
          <span css={DROP_TITLE_STYLE}>Drag here</span>
          <span css={DROP_SUB_STYLE}>
            or <b>browse</b>
          </span>
        </button>
        <span aria-hidden='true' css={DROP_CAP_STYLE}>&nbsp;</span>
      </span>
    </>
  );
};
